import { SelfEntitlementStatus } from '../types';
import { appConfig } from './config';

// Global cache key since backend extracts accountId from JWT
const getCacheKey = () => `marketplace_self_entitlement_status`;
// Session-based cache (no TTL expiration)

/**
 * Fetches self-entitlement status from API and caches it
 * Backend extracts accountId from JWT cookie
 * 
 * @returns Promise with SelfEntitlementStatus or null if failed
 */
export const fetchAndCacheSelfEntitlementStatus = async (
): Promise<SelfEntitlementStatus | null> => {
  try {
    // Backend extracts accountId from JWT; no need to send in query string
    const apiUrl = `${appConfig.api.baseUrl}/api/self-entitle/status`;

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'cookie-based-auth'
      },
      credentials: 'include',
    });


    if (!response.ok) {
      return null;
    }

    const data: SelfEntitlementStatus = await response.json();

    // Session-based cache (no expiration timestamp needed)
    const statusWithTimestamp = {
      ...data,
      lastFetched: Date.now(), // Keep for debugging, but no TTL check
    };

    // Cache in localStorage with global key
    try {
      localStorage.setItem(
        getCacheKey(),
        JSON.stringify(statusWithTimestamp)
      );
    } catch (e) {
      // Silent fail - caching is not critical
    }

    return statusWithTimestamp;
  } catch (error) {
    return null;
  }
};

/**
 * Retrieves cached self-entitlement status if not expired
 * 
 * @returns Cached status or null if expired or not found
 */
export const getCachedSelfEntitlementStatus = (): SelfEntitlementStatus | null => {
  try {
    const cacheKey = getCacheKey();
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }

    const parsed: SelfEntitlementStatus = JSON.parse(cached);
    
    // Session-based cache - no TTL expiration check
    return parsed;
  } catch (error) {
    return null;
  }
};

/**
 * Updates the cached self-entitlement status (used when user self-entitles)
 * 
 * @param status - The new status to cache
 */
export const updateCachedSelfEntitlementStatus = (
  status: SelfEntitlementStatus
): void => {
  const statusWithTimestamp = {
    ...status,
    lastFetched: Date.now(),
  };
  
  try {
    localStorage.setItem(getCacheKey(), JSON.stringify(statusWithTimestamp));
  } catch (error) {
    // Silent fail - caching is not critical
  }
};

/**
 * Clears cached self-entitlement status
 */
export const clearCachedSelfEntitlementStatus = (): void => {
  try {
    localStorage.removeItem(getCacheKey());
  } catch (error) {
    // Silent fail
  }
};
